/** Link entries in Loro (spec §7.6). Human-asserted links are the source of truth (I1). */
import { LoroMap, type LoroDoc, type TreeID } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";
import { blockIri, docIri, linkIri, parseIri, wsLinkIri } from "../lib/iri";

export type LinkObjectKind = "block" | "annotation" | "page" | "iri" | "literal";

export interface LinkObject {
  kind: LinkObjectKind;
  value: string;
  datatype?: string;
  lang?: string;
}

export interface LinkEntry {
  id: string;
  iri: string;
  p: string;
  o: LinkObject;
  createdAt: number;
  createdBy: string;
  derivedFrom?: string | null;
  note?: string | null;
}

function linksMap(node: { data: LoroMap }): LoroMap | null {
  const l = node.data.get("links");
  return l instanceof LoroMap ? l : null;
}

export function readLinks(doc: LoroDoc, docId: string, treeId: TreeID): LinkEntry[] {
  const node = doc.getTree("blocks").getNodeByID(treeId);
  const links = node ? linksMap(node) : null;
  if (!links) return [];
  const out: LinkEntry[] = [];
  for (const id of links.keys()) {
    const entry = links.get(id);
    if (!(entry instanceof LoroMap)) continue;
    const o = entry.get("o") as LinkObject | undefined;
    const p = entry.get("p");
    if (!o || typeof p !== "string") continue;
    out.push({
      id,
      iri: linkIri(docId, id),
      p,
      o,
      createdAt: Number(entry.get("createdAt") ?? 0),
      createdBy: String(entry.get("createdBy") ?? ""),
      derivedFrom: (entry.get("derivedFrom") as string | null) ?? null,
      note: (entry.get("note") as string | null) ?? null,
    });
  }
  return out.sort((a, b) => a.createdAt - b.createdAt);
}

export function addBlockLink(doc: LoroDoc, treeId: TreeID, p: string, o: LinkObject, userId: string, note?: string): string {
  const node = doc.getTree("blocks").getNodeByID(treeId);
  if (!node) throw new Error("block not found");
  const existing = node.data.get("links");
  const links: LoroMap = existing instanceof LoroMap ? existing : node.data.setContainer("links", new LoroMap());
  const id = uuidv7();
  const entry = links.setContainer(id, new LoroMap());
  entry.set("p", p);
  entry.set("o", o);
  entry.set("createdAt", Date.now());
  entry.set("createdBy", userId);
  if (note) entry.set("note", note);
  doc.commit();
  return id;
}

export function removeBlockLink(doc: LoroDoc, treeId: TreeID, linkId: string): void {
  const node = doc.getTree("blocks").getNodeByID(treeId);
  const links = node ? linksMap(node) : null;
  links?.delete(linkId);
  doc.commit();
}

export function addWorkspaceLink(doc: LoroDoc, wsId: string, s: string, p: string, o: LinkObject, userId: string, note?: string): string {
  const links = doc.getMap("links");
  const id = uuidv7();
  const entry = links.setContainer(id, new LoroMap());
  entry.set("s", s);
  entry.set("p", p);
  entry.set("o", o);
  entry.set("createdAt", Date.now());
  entry.set("createdBy", userId);
  if (note) entry.set("note", note);
  doc.commit();
  return wsLinkIri(wsId, id);
}

/** Workspace links (spec §7.5) whose subject is `subject`, e.g. a page (M24), in creation order. */
export function readWorkspaceLinks(doc: LoroDoc, wsId: string, subject: string): LinkEntry[] {
  const links = doc.getMap("links");
  const out: LinkEntry[] = [];
  for (const id of links.keys()) {
    const entry = links.get(id);
    if (!(entry instanceof LoroMap) || entry.get("s") !== subject) continue;
    const o = entry.get("o") as LinkObject | undefined;
    const p = entry.get("p");
    if (!o || typeof p !== "string") continue;
    out.push({
      id,
      iri: wsLinkIri(wsId, id),
      p,
      o,
      createdAt: Number(entry.get("createdAt") ?? 0),
      createdBy: String(entry.get("createdBy") ?? ""),
      derivedFrom: (entry.get("derivedFrom") as string | null) ?? null,
      note: (entry.get("note") as string | null) ?? null,
    });
  }
  return out.sort((a, b) => a.createdAt - b.createdAt);
}

/** Every workspace link, with its subject (the workspace rail reads them all, §8.25). */
export function readAllWorkspaceLinks(doc: LoroDoc): Array<{ id: string; s: string; p: string; o: LinkObject }> {
  const links = doc.getMap("links");
  const out: Array<{ id: string; s: string; p: string; o: LinkObject }> = [];
  for (const id of links.keys()) {
    const entry = links.get(id);
    if (!(entry instanceof LoroMap)) continue;
    const s = entry.get("s");
    const p = entry.get("p");
    const o = entry.get("o") as LinkObject | undefined;
    if (typeof s === "string" && typeof p === "string" && o) out.push({ id, s, p, o });
  }
  return out;
}

export function removeWorkspaceLink(doc: LoroDoc, linkId: string): void {
  doc.getMap("links").delete(linkId);
  doc.commit();
}

/** A page as a link target (spec §7.5 `{kind: "page"}`, M24). */
export const pageTarget = (docId: string): LinkObject => ({ kind: "page", value: docIri(docId) });

export const blockTarget = (docId: string, treeId: string): LinkObject => ({ kind: "block", value: blockIri(docId, treeId) });

/** An IRI typed or pasted into the IRI tab; a KSS block or checklist-item IRI becomes a `block`
 *  target, as if picked in the Blocks tab, so the rail and dangling-link checks treat it as one. */
export function iriTarget(raw: string): LinkObject | null {
  const value = raw.trim();
  if (!value) return null;
  const parsed = parseIri(value);
  if (parsed?.kind === "doc") return { kind: "page", value };
  return { kind: parsed?.kind === "block" || parsed?.kind === "checklist_item" ? "block" : "iri", value };
}
